import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { PLATFORMS, CAMPAIGN_STATUSES, CAMPAIGN_TYPES, US_STATES } from "./constants";

export interface SocialCampaign {
  id?: string;
  name: string;
  description: string | null;
  start_date: string | null;
  end_date: string | null;
  geo_scope: string[];
  platforms: string[];
  status: string;
  campaign_type: string | null;
  created_by: string | null;
  notes: string | null;
}

const BLANK: SocialCampaign = {
  name: '',
  description: null,
  start_date: null,
  end_date: null,
  geo_scope: [],
  platforms: [],
  status: 'draft',
  campaign_type: null,
  created_by: null,
  notes: null,
};

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  campaign?: SocialCampaign | null;
  onSave: (data: SocialCampaign) => void;
  isPending?: boolean;
}

export function CampaignDialog({ open, onOpenChange, campaign, onSave, isPending }: Props) {
  const [form, setForm] = useState<SocialCampaign>(BLANK);
  const [allStates, setAllStates] = useState(false);

  useEffect(() => {
    if (campaign) {
      setForm(campaign);
      setAllStates(campaign.geo_scope.includes('ALL'));
    } else {
      setForm(BLANK);
      setAllStates(false);
    }
  }, [campaign, open]);

  const set = (key: keyof SocialCampaign, value: unknown) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const togglePlatform = (id: string) => {
    set('platforms', form.platforms.includes(id)
      ? form.platforms.filter(p => p !== id)
      : [...form.platforms, id]
    );
  };

  const toggleState = (code: string) => {
    set('geo_scope', form.geo_scope.includes(code)
      ? form.geo_scope.filter(s => s !== code)
      : [...form.geo_scope, code]
    );
  };

  const handleAllStates = (checked: boolean) => {
    setAllStates(checked);
    if (checked) {
      set('geo_scope', ['ALL']);
    } else {
      set('geo_scope', []);
    }
  };

  const handleSubmit = () => {
    if (!form.name.trim()) return;
    onSave(form);
  };

  const isEditing = !!campaign?.id;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Campaign' : 'New Campaign'}</DialogTitle>
          <DialogDescription>
            {isEditing ? 'Update campaign details.' : 'Create a new social media campaign.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Name */}
          <div className="space-y-1">
            <Label>Campaign Name *</Label>
            <Input
              value={form.name}
              onChange={e => set('name', e.target.value)}
              placeholder="Texas Hospital Shame Week"
            />
          </div>

          {/* Description */}
          <div className="space-y-1">
            <Label>Description</Label>
            <Textarea
              value={form.description ?? ''}
              onChange={e => set('description', e.target.value || null)}
              placeholder="What is this campaign about?"
              rows={2}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Status */}
            <div className="space-y-1">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={v => set('status', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CAMPAIGN_STATUSES.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Campaign Type */}
            <div className="space-y-1">
              <Label>Campaign Type</Label>
              <Select
                value={form.campaign_type ?? ''}
                onValueChange={v => set('campaign_type', v || null)}
              >
                <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>
                  {CAMPAIGN_TYPES.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Start Date */}
            <div className="space-y-1">
              <Label>Start Date</Label>
              <Input
                type="date"
                value={form.start_date ?? ''}
                onChange={e => set('start_date', e.target.value || null)}
              />
            </div>

            {/* End Date */}
            <div className="space-y-1">
              <Label>End Date</Label>
              <Input
                type="date"
                value={form.end_date ?? ''}
                onChange={e => set('end_date', e.target.value || null)}
              />
            </div>
          </div>

          {/* Platforms multi-select */}
          <div className="space-y-2">
            <Label>Platforms</Label>
            <div className="flex flex-wrap gap-2">
              {PLATFORMS.map(p => {
                const selected = form.platforms.includes(p.id);
                const displayColor = p.color === '#000000' ? '#374151' : p.color;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => togglePlatform(p.id)}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${
                      selected
                        ? 'text-white border-transparent'
                        : 'bg-background border-border text-muted-foreground hover:border-foreground'
                    }`}
                    style={selected ? { backgroundColor: displayColor } : {}}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Geo Scope */}
          <div className="space-y-2">
            <Label>Geographic Scope</Label>
            <div className="flex items-center gap-2 mb-2">
              <Checkbox
                id="all-states"
                checked={allStates}
                onCheckedChange={checked => handleAllStates(!!checked)}
              />
              <label htmlFor="all-states" className="text-sm cursor-pointer">All States (National)</label>
            </div>
            {!allStates && (
              <div className="flex flex-wrap gap-1.5 p-3 border rounded-md bg-muted/30 max-h-36 overflow-y-auto">
                {US_STATES.map(code => {
                  const selected = form.geo_scope.includes(code);
                  return (
                    <button
                      key={code}
                      type="button"
                      onClick={() => toggleState(code)}
                      className={`px-2 py-0.5 rounded text-xs font-medium border transition-all ${
                        selected
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-background border-border hover:border-primary'
                      }`}
                    >
                      {code}
                    </button>
                  );
                })}
              </div>
            )}
            {!allStates && form.geo_scope.length > 0 && (
              <p className="text-xs text-muted-foreground">{form.geo_scope.length} state{form.geo_scope.length !== 1 ? 's' : ''} selected</p>
            )}
          </div>

          {/* Created By */}
          <div className="space-y-1">
            <Label>Created By</Label>
            <Input
              value={form.created_by ?? ''}
              onChange={e => set('created_by', e.target.value || null)}
              placeholder="Trent"
            />
          </div>

          {/* Notes */}
          <div className="space-y-1">
            <Label>Notes</Label>
            <Textarea
              value={form.notes ?? ''}
              onChange={e => set('notes', e.target.value || null)}
              placeholder="Internal notes..."
              rows={2}
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={isPending || !form.name.trim()}>
            {isPending ? 'Saving...' : isEditing ? 'Update Campaign' : 'Create Campaign'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
